import { useSelector } from "react-redux";
import { IsLoading } from "src/common/components/AppState/IsLoading";
import {
  addMedia,
  getElevenlabsVoices,
  resetTtsPreview,
  uploadAvatar,
} from "../state/gallerySlice";
import { useTranslation } from "react-i18next";
import { Dropdown, Input } from "src/common/components/Input";
import { useEffect, useRef, useState } from "react";
import { loadingSelector, LoadingState } from "src/common/state/slice/appStateSlice";
import { RootState } from "src/common/state/store";
import { GENERATE_MEDIA_PANEL_COMPONENT } from "./GenerateMediaPanel";
import { ITtsPreferences } from "src/model/model";
import { getCurrentUser } from "src/features/profile/state/profileSlice";
import { useAppDispatch } from "src/common/state/hooks";
import { getCurrentModalStateSlider } from "src/common/state/slice/modal/modalSlice";
import { isStringNullOrEmpty } from "src/common/util/isStringNullOrEmpty";
import Spinner from "src/common/components/Loader/Spinner";
import { getUserPreferences } from "src/common/util/getUserPreferences";

export const CreateAvatar = ({ closeSliderAnimation }: { closeSliderAnimation(): any }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const mapElevenlabsVoices = () => {
    return elevenLabsVoices.map((voice) => {
      const localizedVoiceGender = t(`general.${voice.gender}`);
      return { ...voice, value: voice.id, optionText: `${voice.name} (${localizedVoiceGender})` };
    });
  };

  const currentUser = useSelector(getCurrentUser);
  const openSliderId = useSelector(getCurrentModalStateSlider);
  const elevenLabsVoices = useSelector(getElevenlabsVoices);
  const userPreferences = getUserPreferences({ currentUser }) as unknown as ITtsPreferences;

  const { loading } = useSelector((state: RootState) => {
    return {
      loading: loadingSelector(state, GENERATE_MEDIA_PANEL_COMPONENT),
    };
  });

  const [mediaNameToEdit, setMediaNameToEdit] = useState<string>("");
  const [tagEditorKey, setTagEditorKey] = useState(0);
  const [voiceOptions, setVoiceOptions] = useState(mapElevenlabsVoices());
  const [voiceFr, setVoiceFr] = useState(
    userPreferences?.favoriteVoiceId || "pFZP5JQG7iQjIQuC4Bku",
  );
  const [voiceEn, setVoiceEn] = useState(
    userPreferences?.favoriteVoiceId || "pFZP5JQG7iQjIQuC4Bku",
  );
  const [voiceEs, setVoiceEs] = useState(
    userPreferences?.favoriteVoiceId || "pFZP5JQG7iQjIQuC4Bku",
  );
  const [currentAvatarUrl, setCurrentAvatarUrl] = useState<string>("");
  const [isImgPreviewLoading, setIsImgPreviewLoading] = useState<boolean>(false);

  const avatarIframeRef = useRef();
  const avatarImgPreviewRef = useRef();

  if (avatarImgPreviewRef?.current) {
    (avatarImgPreviewRef.current as any).onload = () => {
      setIsImgPreviewLoading(false);
    };
  }

  const resetAvatar = () => {
    setMediaNameToEdit("");
    dispatch(resetTtsPreview());
    setTagEditorKey(tagEditorKey + 1);

    setVoiceFr(userPreferences?.favoriteVoiceId || "pFZP5JQG7iQjIQuC4Bku");
    setVoiceEn(userPreferences?.favoriteVoiceId || "pFZP5JQG7iQjIQuC4Bku");
    setVoiceEs(userPreferences?.favoriteVoiceId || "pFZP5JQG7iQjIQuC4Bku");

    setCurrentAvatarUrl("");

    setVoiceOptions(mapElevenlabsVoices());
  };

  useEffect(() => {
    resetAvatar();
  }, [openSliderId]);

  const parseEvent = (event) => {
    try {
      return JSON.parse(event.data);
    } catch (error) {
      console.log("PARSING ERROR => ", error);
      return null;
    }
  };

  useEffect(() => {
    const receiveMessage = (event) => {
      const res = parseEvent(event);
      if (!res || res?.source !== "readyplayerme") {
        return;
      }
      const frame = avatarIframeRef.current as any;

      switch (res.eventName) {
        case "v1.frame.ready":
          frame.contentWindow.postMessage(
            JSON.stringify({
              target: "readyplayerme",
              type: "subscribe",
              eventName: "v1.**",
            }),
            "*",
          );
          break;
        case "v1.avatar.exported":
          if (
            res.data.url.startsWith("https://") &&
            res.data.url.endsWith(".glb") &&
            res.data.url !== currentAvatarUrl
          ) {
            const glbUrl = res.data.url;
            setCurrentAvatarUrl(glbUrl);

            setIsImgPreviewLoading(true);
            const imgLink = res.data.url.replace(".glb", ".png");
            (avatarImgPreviewRef.current as any).src = imgLink;
          }
          break;
      }
    };

    window.addEventListener("message", receiveMessage);
    return () => {
      window.removeEventListener("message", receiveMessage);
    };
  }, [currentAvatarUrl, dispatch]);

  const saveAvatar = async () => {
    await dispatch(
      uploadAvatar({
        name: mediaNameToEdit,
        avatarUrl: currentAvatarUrl,
        voices: [
          { voiceId: voiceFr, lang: "fr" },
          { voiceId: voiceEn, lang: "en" },
          { voiceId: voiceEs, lang: "es" },
        ],
        componentId: GENERATE_MEDIA_PANEL_COMPONENT,
      }),
    ).then((res) => {
      console.log("UPLOAD AVATAR DONE");
      dispatch(addMedia(res.payload));
      closeSliderAnimation();
    });
  };

  return (
    <div className="h-[calc(90vh-2rem-0.75rem-48px)]">
      <IsLoading
        componentId={GENERATE_MEDIA_PANEL_COMPONENT}
        spinnerPlaceholder
        showSuccess={false}
        spinnerStyle={{ marginTop: "4vh" }}
      >
        <div className="w-full h-3/4 flex justify-center">
          {(!currentAvatarUrl || currentAvatarUrl === "") && (
            <iframe
              id="inlineFrameExample"
              title="Avatar Creator"
              width="100%"
              height="100%"
              src="https://wixar.readyplayer.me/avatar?frameApi"
              allow="clipboard-write"
              ref={avatarIframeRef}
            ></iframe>
          )}
          {isImgPreviewLoading && <Spinner />}
          <img
            ref={avatarImgPreviewRef}
            id="avatar-gen-panel-img-preview"
            className={`max-w-full max-h-full ${
              currentAvatarUrl && currentAvatarUrl !== "" ? "" : "hidden"
            }`}
            src={""}
          ></img>
        </div>

        <div className="h-1/4">
          <div>
            <div>
              <div className="block font-medium text-gray-700 ">{t("general.fileName")}</div>

              <Input
                className="mb-4"
                name="name"
                type="text"
                maxLength={30}
                value={mediaNameToEdit}
                placeholder={t("general.fileName")}
                onChange={(e) => {
                  setMediaNameToEdit(e.target.value);
                }}
                required
                disabled={loading === LoadingState.Loading}
              />
            </div>

            <div className="flex w-full justify-between">
              <Dropdown
                options={voiceOptions}
                label={t("pages.gallery.generateVoicePanel.voice") + " FR"}
                labelFontClassName={"text-md mt-2"}
                onChangeCb={(e: any) => {
                  setVoiceFr(e.target.value);
                }}
                asDefaultValue
                defaultValueIndex={
                  voiceOptions.findIndex((option) => option.value === voiceFr) || 0
                }
              />

              <Dropdown
                options={voiceOptions}
                label={t("pages.gallery.generateVoicePanel.voice") + " EN"}
                labelFontClassName={"text-md mt-2"}
                onChangeCb={(e: any) => {
                  setVoiceEn(e.target.value);
                }}
                asDefaultValue
                defaultValueIndex={
                  voiceOptions.findIndex((option) => option.value === voiceEn) || 0
                }
              />

              <Dropdown
                options={voiceOptions}
                label={t("pages.gallery.generateVoicePanel.voice") + " ES"}
                labelFontClassName={"text-md mt-2"}
                onChangeCb={(e: any) => {
                  setVoiceEs(e.target.value);
                }}
                asDefaultValue
                defaultValueIndex={
                  voiceOptions.findIndex((option) => option.value === voiceEs) || 0
                }
              />
            </div>
            <div className="mt-5">
              <button
                disabled={
                  isStringNullOrEmpty(currentAvatarUrl) || isStringNullOrEmpty(mediaNameToEdit)
                }
                className={"btn-primary-fill mr-3 mt-2 disabled:cursor-not-allowed"}
                onClick={saveAvatar}
              >
                {t("general.save")}
              </button>
            </div>
          </div>
        </div>
      </IsLoading>
    </div>
  );
};
